import {
  BasicCategory,
  FrequencyUnit,
  IItemAndFlavorOptionsWithProduct,
  IItemWithProduct,
  IPatient,
  UserSubscriptionAction,
  PaymentMethodChargeabilityStatus,
  PaymentMethod,
  IAddress,
  UISubscriptionStatus,
  BasicCatalogImage,
  SubscriptionCancelReason,
} from "common/types";
import { Optional } from "common/utils/types";
import { Incentive, Rebate } from "common/types/promotions";
import { ISimpleOrder } from "common/types/orders";

import { ModalStatus } from "client/common/types";

/** Matches subscriptions ClientSubscriptionPaymentMethodSerializer */
export type SubscriptionPaymentMethod = PaymentMethod & {
  chargeability_status: PaymentMethodChargeabilityStatus;
};

/** Matches subscriptions.utils serialize_frequency_option */
interface ISubscriptionFrequencyOption {
  frequency: number;
  interval_unit: FrequencyUnit;
  display: string;
  next_order_dates: string[];
  expected_delivery_dates: string[];
}

/** Matches subscriptions.subscription_pricing_controller SubscriptionDiscountBreakdown  */
export interface SubscriptionDiscountBreakdown {
  display: string;
  discount: number;
  /** The incentive funding this breakdown line; null when this line is rebate-funded. */
  incentive: Incentive | null;
  /** The rebate funding this breakdown line; null when this line is incentive-funded. */
  rebate: Rebate | null;
}

/** Matches subscriptions.subscription_pricing_controller SubscriptionPricing */
export interface SubscriptionPricing {
  unit_price: number;
  quantity: number;
  subtotal: number;
  discount: number;
  handling_fee: number;
  client_handling_fees: number;
  client_purchasing_fee: number;
  shipping: number | null;
  discount_breakdown: SubscriptionDiscountBreakdown[];
  total: number;
}

/**
 * Matches promotions ClientAcceptedRetentionOffer. A subscription's accepted,
 * not-yet-consumed retention offer; used to warn that rescheduling the next order
 * past `invalid_after_date` invalidates the discount.
 */
export type AcceptedRetentionOffer = {
  discount_percent: string | null;
  discount_cents: number | null;
  invalid_after_date: string | null;
};

/**
 * Matches promotions ClientRetentionOfferPreview — GET
 * /api/client/v1/subscriptions/<id>/retention-offer/
 */
export type RetentionOfferPreview = {
  discount_percent: string | null;
  discount_cents: number | null;
  max_discount: number | null;
  invalid_after_date: string | null;
  projected_next_order_data: SubscriptionPricing | null;
};

/** A retention offer that can be shown to the client, with the pricing to show for it */
export type EligibleRetentionOffer = RetentionOfferPreview & {
  projected_next_order_data: SubscriptionPricing;
};

/**
 * The cancel reasons a retention offer can answer, mirroring
 * SubscriptionCancelReason.subscription_retention_offer_eligible_reasons
 */
export type RetentionOfferCancelReason =
  | SubscriptionCancelReason.Expensive
  | SubscriptionCancelReason.SubscriptionNotWanted;

/** Why the client says they are canceling, as collected by the cancel dialog's reason step */
export type SubscriptionCancelRequest = {
  reason: SubscriptionCancelReason;
  reasonOther?: string;
};

/**
 * Matches subscriptions ClientRetentionOfferDecisionSerializer — PUT
 * /api/client/v1/subscriptions/<id>/retention-offer/ request body
 */
export type RetentionOfferDecisionUpdate = {
  accepted: boolean;
  cancel_reason: SubscriptionCancelReason;
};

/** Matches promotions SubscriptionRetentionOfferStatus */
export enum SubscriptionRetentionOfferStatus {
  Accepted = "ACCEPTED",
  Declined = "DECLINED",
}

/**
 * Matches promotions ClientRetentionOfferDecision — PUT
 * /api/client/v1/subscriptions/<id>/retention-offer/ response
 */
export type RetentionOfferDecision = {
  id: number;
  status: SubscriptionRetentionOfferStatus;
};

/**
 * Matches subscriptions ClientSubscriptionDetailSerializer — GET
 * /api/client/v1/subscriptions/<id>/
 */
export interface DetailSubscription {
  id: number;
  item: IItemWithProduct;
  patient: IPatient;
  payment_method: Optional<SubscriptionPaymentMethod>;
  shipping_address: IAddress;
  potential_user_actions: [UserSubscriptionAction, string][];
  last_order?: ISimpleOrder;
  expected_delivery_by: string | null;
  active_expected_delivery_by: string | null;
  flavor_selection: number | null;
  flavors_display: Optional<string>;
  prescription_warning: Optional<string>;
  skip_order_date: string;
  skip_expected_delivery_by: string | null;
  active_frequency: ISubscriptionFrequencyOption;
  recommended_frequency: ISubscriptionFrequencyOption;
  alternative_frequencies: ISubscriptionFrequencyOption[];
  is_active_frequency_valid: boolean;
  visible_status: UISubscriptionStatus;
  has_prescription: Optional<boolean>;
  next_order_data: SubscriptionPricing;
  display_number: string;
  next_order_date: string | null;
  quantity: number;
  prescription_needs_renewal_for_next_order: boolean;
  should_show_employee_pricing_note: boolean;
  accepted_retention_offer: AcceptedRetentionOffer | null;
}

/**
 * Matches subscriptions ClientOnboardingSubscriptionDetailSerializer — GET
 * /api/client/v1/subscriptions/<id>/onboarding/
 */
export interface OnboardingDetailSubscription {
  id: number;
  item: IItemAndFlavorOptionsWithProduct;
  patient: IPatient;
  flavor_selection: number | null;
  active_frequency: ISubscriptionFrequencyOption;
  recommended_frequency: ISubscriptionFrequencyOption;
  alternative_frequencies: ISubscriptionFrequencyOption[];
  is_active_frequency_valid: boolean;
  quantity: number;
}

export type SubscriptionListSearch = {
  visible_status?: UISubscriptionStatus[];
};

/**
 * Matches subscriptions ClientSubscriptionUpdateSerializer — PATCH
 * /api/client/v1/subscriptions/<id>/ request body
 */
export interface SubscriptionUpdate {
  action: UserSubscriptionAction;
  interval_unit?: FrequencyUnit;
  frequency?: number;
  next_order_date?: string;
  expected_delivery_by?: string | null;
  quantity?: number;
  shipping_address_id?: number;
  flavor_selection?: number;
  cancel_reason?: SubscriptionCancelReason;
  cancel_reason_other?: string;
}

/**
 * Matches subscriptions ClientSubscriptionPaymentView.patch — PATCH
 * /api/client/v1/subscriptions/<id>/payment/ request body
 */
export interface SubscriptionPaymentUpdate {
  payment_method_id: number;
}

/**
 * Matches subscriptions ClientSubscriptionPaymentUpdateSerializer — PATCH
 * /api/client/v1/subscriptions/<id>/payment/ response
 */
export type SubscriptionPaymentUpdateResponse = {
  id: number;
  visible_status: UISubscriptionStatus;
  next_order_date: string | null;
  expected_delivery_by: string | null;
};

export enum SubscriptionForm {
  UpdateQuantity = "updateQuantity",
  UpdateFrequency = "updateFrequency",
  UpdateAddress = "updateAddress",
  UpdatePayment = "updatePayment",
  UpdateNextOrder = "updateNextOrder",
  SkipNextOrder = "skipNextOrder",
  Cancel = "cancel",
  Resume = "resume",
}

export type SubscriptionSearch = {
  form?: SubscriptionForm;
  visible?: ModalStatus;
};

/**
 * Matches subscriptions ClientBulkUpdateSubscriptionsSerializer — POST
 * /api/client/v1/subscriptions/bulk-update/ request body
 */
export type BulkUpdateSubscriptions = {
  shipping_address_id?: number;
  payment_method_id?: number;
} & (
  | { subscription_ids: number[] }
  | {
      visible_status: UISubscriptionStatus[];
      exclude_shipping_address_id?: number;
      exclude_payment_method_id?: number;
      exclude_subscription_ids?: number[];
    }
);

/** Matches query params for GET /api/client/v1/subscriptions/basic/ */
export type BasicSubscriptionsSearch = {
  visible_status?: UISubscriptionStatus[];
  exclude_shipping_address_id?: number;
  exclude_payment_method_id?: number;
  page?: number;
  page_size?: number;
};

/** Matches subscriptions ClinicClientBasicSubscriptionPatientSerializer */
export type BasicSubscriptionPatient = {
  id: number;
  name_display: string;
};

/** Matches subscriptions SubscriptionListItemSerializer */
export type BasicSubscriptionItem = {
  id: number;
  name: string;
  full_name: string;
  product_line_name: string;
  product_line_relative_name: string;
  image: Optional<BasicCatalogImage>;
};

/** Matches subscriptions ClientBasicSubscriptionSerializer */
export type BasicSubscription = {
  id: number;
  patient: BasicSubscriptionPatient;
  item: BasicSubscriptionItem;
  shipping_address: Optional<IAddress>;
  payment_method: Optional<SubscriptionPaymentMethod>;
};

/**
 * Matches subscriptions ClientBulkUpdateSubscriptionsView.post — POST
 * /api/client/v1/subscriptions/bulk-update/ response
 */
export type BulkUpdateSubscriptionsResponse = {
  updated_subscription_count: number;
};

/* Matches subscriptions ClientSubscriptionListProductLineSerializer */
export interface SubscriptionListProductLine {
  id: number;
  name: string;
  brand_name: string;
  short_description: Optional<string>;
  prescription_required: boolean;
  categories: BasicCategory[];
  primary_category_name: string;
}

/* Matches subscriptions ClientSubscriptionListProductSerializer */
export interface SubscriptionListProduct {
  id: number;
  name: string;
  product_line: SubscriptionListProductLine;
}

/* Matches subscriptions ClientSubscriptionListItemSerializer */
export interface SubscriptionListItem {
  id: number;
  name: string;
  msrp: number;
  full_name: string;
  image: Optional<BasicCatalogImage>;
  product: SubscriptionListProduct;
  days_supply: number;
  duration_value: number;
  duration_unit: FrequencyUnit;
}

/* Matches subscriptions ClientSubscriptionListPatientSerializer */
export interface SubscriptionListPatient {
  id: number;
  name_display: string;
}

/* Matches subscriptions ClientSubscriptionListSerializer.get_next_order_data */
export interface SubscriptionListNextOrderData {
  unit_price: number;
  discount: number;
  subtotal: number;
}

/**
 * Matches subscriptions ClientSubscriptionListSerializer — GET
 * /api/client/v1/subscriptions/, and the `subscriptions` array of the dashboard summary
 * (GET /api/client/v1/dashboard/summary/, nested by ClientDashboardSummarySerializer).
 */
export interface ListSubscription {
  id: number;
  item: SubscriptionListItem;
  patient: SubscriptionListPatient;
  payment_method: Optional<SubscriptionPaymentMethod>;
  shipping_address: IAddress;
  potential_user_actions: [UserSubscriptionAction, string][];
  last_order?: ISimpleOrder;
  flavor_selection: number | null;
  flavors_display: Optional<string>;
  active_frequency: ISubscriptionFrequencyOption;
  recommended_frequency: ISubscriptionFrequencyOption;
  alternative_frequencies: ISubscriptionFrequencyOption[];
  is_active_frequency_valid: boolean;
  visible_status: UISubscriptionStatus;
  next_order_data: SubscriptionListNextOrderData;
  next_order_date: string | null;
  expected_delivery_by: string | null;
  active_expected_delivery_by: string | null;
  quantity: number;
  prescription_needs_renewal_for_next_order: boolean;
  has_prescription: Optional<boolean>;
  accepted_retention_offer: AcceptedRetentionOffer | null;
}
